import { Layout } from "@/components/layout/Layout";
import { ScrollFadeIn } from "@/components/ScrollFadeIn";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const BG_IMAGE = "https://a.lovart.ai/artifacts/agent/D9CQqLhKGo8NYzqi.jpeg";

/* ─── Persistent background layer ─────────────────────────────────── */
function PageBackground() {
  return (
    <div className="fixed inset-0 -z-10">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('${BG_IMAGE}')` }}
      />
      <div className="absolute inset-0 bg-background/[0.82]" />
    </div>
  );
}

/* ─── Section 1 · Hero ──────────────────────────────────────────────── */
function Hero() {
  return (
    <section className="relative flex min-h-[85vh] items-center justify-center px-6 py-32 overflow-hidden">
      {/* Stronger image reveal in hero */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('${BG_IMAGE}')` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/50 to-background/90" />
      <div className="relative z-10 mx-auto max-w-3xl text-center">
        <ScrollFadeIn>
          <h1 className="font-serif text-4xl leading-tight tracking-tight text-foreground md:text-5xl lg:text-6xl">
            Furniture &amp; Soft Furnishings, composed&nbsp;as&nbsp;a&nbsp;system.
          </h1>
        </ScrollFadeIn>
        <ScrollFadeIn delay={120}>
          <p className="mx-auto mt-8 max-w-2xl font-sans text-base leading-relaxed text-muted-foreground md:text-lg">
            Furniture is not a collection of objects. It defines proportion,
            comfort, rhythm, and how a space is actually lived in. MINZO helps
            designers source furniture and soft furnishings that align in
            material, scale, and spatial intent.
          </p>
        </ScrollFadeIn>
        <ScrollFadeIn delay={240}>
          <Button asChild size="lg" className="mt-10 uppercase tracking-widest text-xs font-sans">
            <Link to="/#concierge">
              Start an inquiry <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Link>
          </Button>
        </ScrollFadeIn>
      </div>
    </section>
  );
}

/* ─── Section 2 · Intro ─────────────────────────────────────────────── */
function Intro() {
  return (
    <section className="relative px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <div className="mx-auto max-w-3xl">
            <h2 className="font-serif text-3xl text-foreground md:text-4xl">
              More than selecting pieces.
            </h2>
            <div className="mt-8 space-y-5 font-sans text-base leading-relaxed text-muted-foreground md:text-[17px]">
              <p>
                Furniture often feels fragmented when sourced loosely — proportions
                clash, material languages compete, upholstery lacks depth, and
                finishes sit apart rather than together. The result is a space that
                looks styled rather than composed.
              </p>
              <p>
                MINZO helps designers source furniture and soft furnishings as a
                coordinated system — so every piece, from anchor furniture to
                textiles and rugs, holds together in material, scale, and tone.
              </p>
            </div>
          </div>
        </ScrollFadeIn>
      </div>
    </section>
  );
}

/* ─── Section 3 · What We Support ────────────────────────────────── */
const supports = [
  {
    title: "Core Furniture",
    body: "Sofas, dining tables, beds, storage systems, and casegoods — selected for proportion, material quality, and how they anchor the spatial composition.",
  },
  {
    title: "Soft Furnishings",
    body: "Rugs, curtains, cushions, throws, and bedding — sourced for texture, weight, and tonal coherence with the broader material palette.",
  },
  {
    title: "Material & Finish Coordination",
    body: "Aligning wood tones, metal finishes, fabric textures, and stone elements across furniture and soft goods — so nothing competes and everything belongs.",
  },
  {
    title: "Custom & Adaptation",
    body: "When standard dimensions, finishes, or upholstery options don't match the project — we coordinate bespoke solutions with trusted production partners.",
  },
];

function WhatWeSupport() {
  return (
    <section className="relative border-t border-border/40 px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/75 backdrop-blur-[2px]" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <h2 className="font-serif text-3xl text-foreground md:text-4xl">
            What we support
          </h2>
        </ScrollFadeIn>
        <div className="mt-16 grid gap-12 sm:grid-cols-2">
          {supports.map((s, i) => (
            <ScrollFadeIn key={s.title} delay={i * 100}>
              <div>
                <h3 className="font-serif text-xl text-foreground">{s.title}</h3>
                <p className="mt-3 font-sans text-sm leading-relaxed text-muted-foreground md:text-base">
                  {s.body}
                </p>
              </div>
            </ScrollFadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Section 4 · Why Composition Matters ────────────────────────── */
function WhyComposition() {
  return (
    <section className="relative px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <div className="mx-auto max-w-3xl">
            <h2 className="font-serif text-3xl text-foreground md:text-4xl">
              Why composition matters
            </h2>
            <div className="mt-8 space-y-5 font-sans text-base leading-relaxed text-muted-foreground md:text-[17px]">
              <p>
                A well-chosen sofa in isolation does not make a room. What matters
                is the relationship — between scale and ceiling height, between
                fabric weight and wall texture, between the proportion of a dining
                table and the rhythm of the chairs around it.
              </p>
              <p>
                Color tone, material balance, spacing, and interaction with
                architecture are what separate a composed interior from a decorated
                one. Composition means every element participates in a coherent
                whole — not just occupies a position.
              </p>
            </div>
          </div>
        </ScrollFadeIn>
      </div>
    </section>
  );
}

/* ─── Section 5 · Typical Problems ───────────────────────────────── */
const problems = [
  "Sourcing feels scattered across too many suppliers and catalogues.",
  "Brands don't align — materials, proportions, and finishes compete.",
  "Proportions look right in renders but feel wrong in the real space.",
  "Upholstery and textiles lack material depth and tactile quality.",
  "Custom pieces are needed but production coordination is unclear.",
  "The space feels styled, not resolved — assembled rather than composed.",
];

function TypicalProblems() {
  return (
    <section className="relative border-t border-border/40 px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/75 backdrop-blur-[2px]" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <h2 className="font-serif text-3xl text-foreground md:text-4xl">
            Typical problems we help solve
          </h2>
        </ScrollFadeIn>
        <ul className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {problems.map((p, i) => (
            <ScrollFadeIn key={i} delay={i * 80}>
              <li className="flex items-start gap-3 font-sans text-sm leading-relaxed text-muted-foreground md:text-base">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/50" />
                {p}
              </li>
            </ScrollFadeIn>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ─── Section 6 · How We Work ────────────────────────────────────── */
const steps = [
  { num: "01", title: "Understand spatial language", body: "We begin with the project's atmosphere, material direction, and how the space will be lived in — not a product wishlist." },
  { num: "02", title: "Define key furniture anchors", body: "Identifying the pieces that set the spatial rhythm — the sofa, the dining table, the bed — and building outward from there." },
  { num: "03", title: "Build material coherence", body: "Aligning wood tones, fabric textures, metal finishes, and rug compositions so everything belongs to the same design language." },
  { num: "04", title: "Source and adapt", body: "Finding the right products — or coordinating custom production — and managing procurement through to delivery." },
];

function HowWeWork() {
  return (
    <section className="relative px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <h2 className="font-serif text-3xl text-foreground md:text-4xl">
            How we work
          </h2>
        </ScrollFadeIn>
        <div className="mt-16 grid gap-12 sm:grid-cols-2">
          {steps.map((s, i) => (
            <ScrollFadeIn key={s.num} delay={i * 100}>
              <div>
                <span className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-primary/60">
                  {s.num}
                </span>
                <h3 className="mt-2 font-serif text-xl text-foreground">{s.title}</h3>
                <p className="mt-3 font-sans text-sm leading-relaxed text-muted-foreground md:text-base">
                  {s.body}
                </p>
              </div>
            </ScrollFadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Section 7 · System Areas ───────────────────────────────────── */
const areas = [
  "Sofas & lounge seating",
  "Dining & tables",
  "Beds & bedroom systems",
  "Storage & casegoods",
  "Custom furniture",
  "Rugs & carpets",
  "Curtains & drapery",
  "Upholstery & textiles",
  "Bedding systems",
];

function SystemAreas() {
  return (
    <section className="relative border-t border-border/40 px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/75 backdrop-blur-[2px]" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <h2 className="font-serif text-3xl text-foreground md:text-4xl">
            System areas
          </h2>
        </ScrollFadeIn>
        <div className="mt-14 flex flex-wrap gap-3">
          {areas.map((a, i) => (
            <ScrollFadeIn key={a} delay={i * 50}>
              <span className="inline-block border border-border/60 bg-background/50 backdrop-blur-sm px-5 py-2.5 font-sans text-xs uppercase tracking-[0.12em] text-foreground/70">
                {a}
              </span>
            </ScrollFadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Section 8 · Brand Positioning ──────────────────────────────── */
function BrandPositioning() {
  return (
    <section className="relative px-6 py-24 md:py-32">
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
      <div className="relative mx-auto max-w-[1100px]">
        <ScrollFadeIn>
          <div className="mx-auto max-w-3xl">
            <h2 className="font-serif text-3xl text-foreground md:text-4xl">
              For designers who value restraint&nbsp;and&nbsp;cohesion
            </h2>
            <p className="mt-8 font-sans text-base leading-relaxed text-muted-foreground md:text-[17px]">
              Precision over excess. Calm continuity over visual noise. The right
              furniture system doesn't seek attention — it resolves the space.
              Every material, proportion, and texture should feel intentional,
              quiet, and composed as part of the architecture.
            </p>
          </div>
        </ScrollFadeIn>
      </div>
    </section>
  );
}

/* ─── Section 9 · Final CTA ──────────────────────────────────────── */
function FinalCTA() {
  return (
    <section className="relative border-t border-border/40 px-6 py-24 md:py-32">
      {/* Slightly reveal image again at the end */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('${BG_IMAGE}')` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/70 to-background/80" />
      <div className="relative mx-auto max-w-3xl text-center">
        <ScrollFadeIn>
          <h2 className="font-serif text-3xl text-foreground md:text-4xl">
            Working on a furniture-led project?
          </h2>
        </ScrollFadeIn>
        <ScrollFadeIn delay={100}>
          <p className="mx-auto mt-6 max-w-xl font-sans text-base leading-relaxed text-muted-foreground">
            Share your drawings, references, or material direction. We can
            support furniture selection, textile coordination, and integrated
            sourcing based on the needs of the project.
          </p>
        </ScrollFadeIn>
        <ScrollFadeIn delay={200}>
          <Button asChild size="lg" className="mt-10 uppercase tracking-widest text-xs font-sans">
            <Link to="/#concierge">
              Start a furniture inquiry <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Link>
          </Button>
        </ScrollFadeIn>
      </div>
    </section>
  );
}

/* ─── Page ────────────────────────────────────────────────────────── */
export default function FurnitureSoftFurnishings() {
  return (
    <Layout>
      <PageBackground />
      <Hero />
      <Intro />
      <WhatWeSupport />
      <WhyComposition />
      <TypicalProblems />
      <HowWeWork />
      <SystemAreas />
      <BrandPositioning />
      <FinalCTA />
    </Layout>
  );
}
